import React, { useState, useMemo, useCallback, useEffect, forwardRef, useImperativeHandle } from 'react';
import { isMobile, isTablet } from 'react-device-detect';
import Select from 'react-select';
import { useDeviceInfo } from '../../DeviceInfo';
import { useTaggingData } from './hooks/useTaggingData';
import CompletionBars from './components/CompletionBars';
import HeatmapTreeGrid from './components/HeatmapTreeGrid';
import DetailGrid from './components/DetailGrid';
import './taggingPatrol.scss';

const siteSelectStyles = {
    container: (base) => ({ ...base, flex: 1, minWidth: 0 }),
    control: (base) => ({
        ...base,
        minHeight: '34px',
        fontSize: '12px',
        borderColor: 'rgba(255,255,255,0.12)',
        backgroundColor: 'rgba(255,255,255,0.03)',
        borderRadius: '4px',
        boxShadow: 'none',
        flexWrap: 'nowrap',
        '&:hover': { borderColor: 'rgba(255,255,255,0.25)' }
    }),
    singleValue: (base) => ({ ...base, color: 'white', fontSize: '12px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }),
    placeholder: (base) => ({ ...base, color: 'rgba(255,255,255,0.5)', fontSize: '12px' }),
    input: (base) => ({ ...base, color: 'white', fontSize: '12px' }),
    menu: (base) => ({ ...base, zIndex: 9999, backgroundColor: '#1e163c', border: '1px solid rgba(255,255,255,0.12)', minWidth: '200px' }),
    option: (base, state) => ({
        ...base,
        fontSize: '12px',
        padding: '6px 10px',
        backgroundColor: state.isSelected ? '#CB2C84' : state.isFocused ? 'rgba(0,93,229,0.1)' : 'transparent',
        color: 'white',
        '&:active': { backgroundColor: 'rgba(0,93,229,0.2)' }
    }),
    indicatorSeparator: () => ({ display: 'none' }),
    dropdownIndicator: (base) => ({ ...base, padding: '4px', color: 'rgba(255,255,255,0.5)', '&:hover': { color: 'white' } }),
    clearIndicator: (base) => ({ ...base, padding: '4px', color: 'rgba(255,255,255,0.5)', '&:hover': { color: 'white' } }),
};

const toISODate = (d) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
};

const fmtDate = (isoStr) => {
    if (!isoStr) return '';
    const parts = isoStr.split('-');
    return `${parts[2]}/${parts[1]}/${parts[0]}`;
};

const daysBetween = (a, b) => {
    const msDay = 86400000;
    return Math.round((new Date(b) - new Date(a)) / msDay);
};

const addDays = (isoStr, days) => {
    const d = new Date(isoStr);
    d.setDate(d.getDate() + days);
    return toISODate(d);
};

const TaggingPatrol = forwardRef(({
    preloadedData,
    setPreloadedData,
    dataLoaded,
    setDataLoaded,
}, ref) => {
    const { deviceInfo } = useDeviceInfo();
    const isMobileView = isMobile || isTablet;

    const {
        taggingData,
        loading,
        error,
        dataFetched,
        refresh,
    } = useTaggingData(deviceInfo, preloadedData);

    const [selectedSite, setSelectedSite] = useState(null);
    const [activeFilter, setActiveFilter] = useState(null);
    const [mobileTab, setMobileTab] = useState('bars');

    const dateBounds = useMemo(() => {
        if (!taggingData || taggingData.length === 0) return null;
        let min = null;
        let max = null;
        taggingData.forEach(row => {
            const d = row.dueDate;
            if (!d) return;
            const date = new Date(d);
            if (!min || date < min) min = date;
            if (!max || date > max) max = date;
        });
        if (!min || !max) return null;
        return { min: toISODate(min), max: toISODate(max) };
    }, [taggingData]);

    const [dateFrom, setDateFrom] = useState(null);
    const [dateTo, setDateTo] = useState(null);

    useEffect(() => {
        if (dateBounds && !dateFrom) {
            setDateFrom(dateBounds.min);
            const totalDays = daysBetween(dateBounds.min, dateBounds.max);
            setDateTo(totalDays <= 18 ? dateBounds.max : addDays(dateBounds.min, 17));
        }
    }, [dateBounds, dateFrom]);

    useEffect(() => {
        if (dataFetched && !dataLoaded && taggingData.length > 0 && setPreloadedData && setDataLoaded) {
            setPreloadedData({ taggingData });
            setDataLoaded(true);
        }
    }, [dataFetched, dataLoaded, taggingData, setPreloadedData, setDataLoaded]);

    const siteOptions = useMemo(() => {
        if (!taggingData || taggingData.length === 0) return [];
        const unique = [...new Set(taggingData.map(d => d.siteName).filter(Boolean))].sort();
        return unique.map(name => ({ value: name, label: name }));
    }, [taggingData]);

    const dateRange = dateFrom && dateTo ? { from: dateFrom, to: dateTo } : null;

    const handleBarClick = useCallback((site, status) => {
        setActiveFilter(prev => {
            if (prev?.site === site && prev?.status === status && prev?.source === 'chart') {
                return null;
            }
            if (isMobileView) setMobileTab('heatmap');
            return { site, status, source: 'chart' };
        });
    }, [isMobileView]);

    const handleCellClick = useCallback((filter) => {
        setActiveFilter(prev => {
            const isSame = prev?.site === filter.site
                && prev?.task === filter.task
                && prev?.staff === filter.staff
                && prev?.date === filter.date
                && prev?.source === 'heatmap';
            if (isSame) return null;
            if (isMobileView) setMobileTab('detail');
            return { ...filter, source: 'heatmap' };
        });
    }, [isMobileView]);

    const handleHeatmapRowClick = useCallback((filter) => {
        setActiveFilter(prev => {
            const isSame = prev?.site === filter.site
                && prev?.task === filter.task
                && prev?.staff === filter.staff
                && prev?.source === 'heatmap';
            if (isSame) return null;
            return { ...filter, source: 'heatmap' };
        });
    }, []);

    const handleDetailRowClick = useCallback((filter) => {
        setActiveFilter(prev => {
            const isSame = prev?.site === filter.site
                && prev?.task === filter.task
                && prev?.staff === filter.staff
                && prev?.date === filter.date
                && prev?.source === 'detail';
            if (isSame) return null;
            return { ...filter, source: 'detail' };
        });
    }, []);

    const handleDrillChange = useCallback((drillPath) => {
        if (drillPath.length === 0) {
            setActiveFilter(null);
            return;
        }
        const site = drillPath[0];
        const filter = { site, source: 'chart' };
        if (drillPath.length >= 2) filter.task = drillPath[1];
        setActiveFilter(filter);
    }, []);

    const clearFilter = useCallback(() => setActiveFilter(null), []);

    const handleSiteChange = useCallback((val) => {
        setSelectedSite(val);
        setActiveFilter(null);
    }, []);

    const onSliderFrom = useCallback((e) => {
        if (!dateBounds) return;
        const val = parseInt(e.target.value, 10);
        const toDay = dateTo ? daysBetween(dateBounds.min, dateTo) : 0;
        if (val <= toDay) {
            setDateFrom(addDays(dateBounds.min, val));
        }
    }, [dateBounds, dateTo]);

    const onSliderTo = useCallback((e) => {
        if (!dateBounds) return;
        const val = parseInt(e.target.value, 10);
        const fromDay = dateFrom ? daysBetween(dateBounds.min, dateFrom) : 0;
        if (val >= fromDay) {
            setDateTo(addDays(dateBounds.min, val));
        }
    }, [dateBounds, dateFrom]);

    const onDateFrom = useCallback((e) => {
        const val = e.target.value;
        if (dateBounds && val >= dateBounds.min && val <= (dateTo || dateBounds.max)) {
            setDateFrom(val);
        }
    }, [dateBounds, dateTo]);

    const onDateTo = useCallback((e) => {
        const val = e.target.value;
        if (dateBounds && val >= (dateFrom || dateBounds.min) && val <= dateBounds.max) {
            setDateTo(val);
        }
    }, [dateBounds, dateFrom]);

    useImperativeHandle(ref, () => ({
        renderFilters: () => (
            <div className="sidebar-filter-section">
                <div className="sidebar-filter-item">
                    <label>Site</label>
                    <Select
                        value={selectedSite}
                        onChange={handleSiteChange}
                        options={siteOptions}
                        placeholder="All Sites"
                        isClearable
                        isSearchable
                        classNamePrefix="react-select"
                        styles={siteSelectStyles}
                    />
                </div>
                {dateBounds && (<>
                    <div className="sidebar-filter-item">
                        <label>From</label>
                        <input
                            type="date"
                            className="form-control tagging-patrol__date-input"
                            value={dateFrom || ''}
                            min={dateBounds.min}
                            max={dateTo || dateBounds.max}
                            onChange={onDateFrom}
                        />
                    </div>
                    <div className="sidebar-filter-item">
                        <label>To</label>
                        <input
                            type="date"
                            className="form-control tagging-patrol__date-input"
                            value={dateTo || ''}
                            min={dateFrom || dateBounds.min}
                            max={dateBounds.max}
                            onChange={onDateTo}
                        />
                    </div>
                </>)}
            </div>
        ),
    }), [selectedSite, siteOptions, dateBounds, dateFrom, dateTo, handleSiteChange, onDateFrom, onDateTo]);

    if (loading) {
        return (
            <div className="text-center p-5">
                <div className="small-spinner">
                    <span></span><span></span><span></span><span></span>
                </div>
                <p className="mt-3" style={{ color: 'white' }}>Loading tagging data...</p>
            </div>
        );
    }

    if (error) {
        return (
            <div className="text-center p-5" style={{ color: 'white' }}>
                <p>Error loading data: {error}</p>
                <button className="tagging-patrol__retry-btn" onClick={refresh}>Retry</button>
            </div>
        );
    }

    if (!taggingData || taggingData.length === 0) {
        return (
            <div className="text-center p-5" style={{ color: 'white' }}>
                <p>No tagging/patrol data available.</p>
            </div>
        );
    }

    const siteFilterValue = selectedSite?.value || null;
    const totalDays = dateBounds ? daysBetween(dateBounds.min, dateBounds.max) : 0;
    const sliderFromValue = dateBounds && dateFrom ? daysBetween(dateBounds.min, dateFrom) : 0;
    const sliderToValue = dateBounds && dateTo ? daysBetween(dateBounds.min, dateTo) : 0;

    const renderFilterBar = () => (
        <div className="tagging-patrol__filters">
            <div className="tagging-patrol__site-filter">
                <label className="tagging-patrol__filter-label">Site</label>
                <Select
                    value={selectedSite}
                    onChange={handleSiteChange}
                    options={siteOptions}
                    placeholder="All Sites"
                    isClearable
                    isSearchable
                    classNamePrefix="react-select"
                    styles={siteSelectStyles}
                />
            </div>
            {dateBounds && (
                <div className="tagging-patrol__date-filter">
                    <label className="tagging-patrol__filter-label">Date</label>
                    <div className="tagging-patrol__date-inputs">
                        <input
                            type="date"
                            className="tagging-patrol__date-input"
                            value={dateFrom || ''}
                            min={dateBounds.min}
                            max={dateTo || dateBounds.max}
                            onChange={onDateFrom}
                        />
                        <input
                            type="date"
                            className="tagging-patrol__date-input"
                            value={dateTo || ''}
                            min={dateFrom || dateBounds.min}
                            max={dateBounds.max}
                            onChange={onDateTo}
                        />
                    </div>
                    <div className="tagging-patrol__slider-row">
                        <div className="tagging-patrol__dual-slider">
                            <div
                                className="tagging-patrol__slider-track"
                                style={{
                                    left: `${totalDays > 0 ? (sliderFromValue / totalDays) * 100 : 0}%`,
                                    right: `${totalDays > 0 ? 100 - (sliderToValue / totalDays) * 100 : 0}%`,
                                }}
                            />
                            <input
                                type="range"
                                className="tagging-patrol__slider tagging-patrol__slider--from"
                                min={0}
                                max={totalDays}
                                value={sliderFromValue}
                                onChange={onSliderFrom}
                            />
                            <input
                                type="range"
                                className="tagging-patrol__slider tagging-patrol__slider--to"
                                min={0}
                                max={totalDays}
                                value={sliderToValue}
                                onChange={onSliderTo}
                            />
                        </div>
                    </div>
                </div>
            )}
            {activeFilter && (
                <div className="tagging-patrol__active-filter">
                    <span>
                        {activeFilter.site && <strong>{activeFilter.site}</strong>}
                        {activeFilter.task && <> &rsaquo; {activeFilter.task}</>}
                        {activeFilter.staff && <> &rsaquo; {activeFilter.staff}</>}
                        {activeFilter.date && <> &mdash; {fmtDate(activeFilter.date)}</>}
                        {activeFilter.status && <> &mdash; {activeFilter.status}</>}
                    </span>
                    <button className="tagging-patrol__clear-btn" onClick={clearFilter}>
                        Clear
                    </button>
                </div>
            )}
        </div>
    );

    if (isMobileView) {
        return (
            <div className="tagging-patrol tagging-patrol--mobile">
                <div className="tagging-patrol__tab-bar">
                    <button
                        className={`tagging-patrol__tab ${mobileTab === 'bars' ? 'active' : ''}`}
                        onClick={() => setMobileTab('bars')}
                    >
                        Bars
                    </button>
                    <button
                        className={`tagging-patrol__tab ${mobileTab === 'heatmap' ? 'active' : ''}`}
                        onClick={() => setMobileTab('heatmap')}
                    >
                        Heatmap
                    </button>
                    <button
                        className={`tagging-patrol__tab ${mobileTab === 'detail' ? 'active' : ''}`}
                        onClick={() => setMobileTab('detail')}
                    >
                        Detail
                        {activeFilter && <span className="tagging-patrol__tab-dot" />}
                    </button>
                </div>
                {activeFilter && (
                    <div className="tagging-patrol__active-filter">
                        <span>
                            {activeFilter.site && <strong>{activeFilter.site}</strong>}
                            {activeFilter.task && <> &rsaquo; {activeFilter.task}</>}
                            {activeFilter.staff && <> &rsaquo; {activeFilter.staff}</>}
                            {activeFilter.date && <> &mdash; {fmtDate(activeFilter.date)}</>}
                            {activeFilter.status && <> &mdash; {activeFilter.status}</>}
                        </span>
                        <button className="tagging-patrol__clear-btn" onClick={clearFilter}>
                            Clear
                        </button>
                    </div>
                )}
                <div className="tagging-patrol__mobile-panel">
                    {mobileTab === 'bars' && (
                        <CompletionBars
                            data={taggingData}
                            siteFilter={siteFilterValue}
                            onBarClick={handleBarClick}
                            onDrillChange={handleDrillChange}
                            activeFilter={activeFilter}
                        />
                    )}
                    {mobileTab === 'heatmap' && (
                        <HeatmapTreeGrid
                            data={taggingData}
                            siteFilter={siteFilterValue}
                            dateRange={dateRange}
                            onCellClick={handleCellClick}
                            onRowClick={handleHeatmapRowClick}
                            activeFilter={activeFilter}
                        />
                    )}
                    {mobileTab === 'detail' && (
                        <DetailGrid
                            data={taggingData}
                            siteFilter={siteFilterValue}
                            activeFilter={activeFilter}
                            onRowClick={handleDetailRowClick}
                        />
                    )}
                </div>
            </div>
        );
    }

    return (
        <div className="tagging-patrol">
            {renderFilterBar()}
            <div className="tagging-patrol__top-row">
                <div className="tagging-patrol__bars-panel">
                    <CompletionBars
                        data={taggingData}
                        siteFilter={siteFilterValue}
                        onBarClick={handleBarClick}
                        onDrillChange={handleDrillChange}
                        activeFilter={activeFilter}
                    />
                </div>
                <div className="tagging-patrol__heatmap-panel">
                    <HeatmapTreeGrid
                        data={taggingData}
                        siteFilter={siteFilterValue}
                        dateRange={dateRange}
                        onCellClick={handleCellClick}
                        onRowClick={handleHeatmapRowClick}
                        activeFilter={activeFilter}
                    />
                </div>
            </div>
            <div className="tagging-patrol__detail-panel">
                <DetailGrid
                    data={taggingData}
                    siteFilter={siteFilterValue}
                    activeFilter={activeFilter}
                    onRowClick={handleDetailRowClick}
                />
            </div>
        </div>
    );
});

export default TaggingPatrol;
